// clientUtils.ts

// Define a simplified version of Client for status checks
interface ClientStatus {
  assessed?: boolean;
  termsAccepted?: boolean;
  assignedCounsellor?: boolean | string;
  assignedCounsellorName?: string;
  currentCounsellorId?: string;
  counsellorHistory?: Array<{
    counsellorId: any;
    assignedDate: string;
    endDate?: string;
  }>;
  contractReviewedBy?: Array<{
    counsellorId: any;
    reviewedAt: string;
  }>;
  isArchived?: boolean;
  status?: string;
  isExistingClient?: boolean;
  existingClientOnboarded?: boolean;
  hasPendingSessionNotes?: boolean;
}

export type ClientJourneyStatus = 
  | 'Awaiting Onboarding'
  | 'Pending Contract'
  | 'Awaiting Counsellor'
  | 'Assigned'
  | 'Awaiting Appointment'
  | 'Awaiting Session Notes'
  | 'Pending Archive'
  | 'Completed'
  | 'Cancelled'
  | 'Archived';

type StatusStyle = {
  backgroundColor: string;
  color: string;
};

export const getClientStatus = (client: ClientStatus): ClientJourneyStatus => {
  if (!client) return 'Awaiting Onboarding';
  
  if (client.isArchived) return 'Archived';
  if (client.status === 'pending_archive') return 'Pending Archive';
  if (client.status === 'completed') return 'Completed';
  if (client.status === 'cancelled') return 'Cancelled';
  
  // Check if existing client awaiting onboarding BEFORE assessment check
  if (client.isExistingClient && !client.existingClientOnboarded) {
    return 'Awaiting Onboarding';
  }
  
  if (!client.assessed) return 'Awaiting Onboarding';
  if (!client.termsAccepted) return 'Pending Contract';
  
  // Check for counsellor assignment using multiple sources
  let hasAssignedCounsellor = false;
  
  // First check currentCounsellorId (most reliable)
  if (client.currentCounsellorId) {
    hasAssignedCounsellor = true;
  }
  
  // Then check counsellorHistory for active assignment
  if (!hasAssignedCounsellor && client.counsellorHistory && client.counsellorHistory.length > 0) {
    const currentAssignment = client.counsellorHistory[client.counsellorHistory.length - 1];
    hasAssignedCounsellor = currentAssignment && currentAssignment.counsellorId && !currentAssignment.endDate;
  }
  
  // Fallback to other fields
  if (!hasAssignedCounsellor) {
    hasAssignedCounsellor = !!(client.assignedCounsellorName || 
      (typeof client.assignedCounsellor === 'string' ? client.assignedCounsellor : client.assignedCounsellor));
  }
  
  if (!hasAssignedCounsellor) return 'Awaiting Counsellor';
  
  // Check if counsellor has reviewed contract (skip for existing clients who were onboarded before contract review system)
  const isExistingOnboardedClient = client.isExistingClient && client.existingClientOnboarded;
  const contractReviewedByCounsellor = client.contractReviewedBy?.some(
    review => review.counsellorId?.toString() === client.currentCounsellorId?.toString()
  );
  
  if (!contractReviewedByCounsellor && !isExistingOnboardedClient) return 'Assigned';
  
  // Check for pending session notes (scheduled time elapsed but no notes)
  if (client.hasPendingSessionNotes) return 'Awaiting Session Notes';
  
  return 'Awaiting Appointment';
};

export const getClientStatusChipStyles = (client: ClientStatus): StatusStyle => {
  const statusStyles: Record<ClientJourneyStatus, StatusStyle> = {
    'Awaiting Onboarding': {
      backgroundColor: '#fef3c7',  // amber
      color: '#92400e',
    },
    'Pending Contract': {
      backgroundColor: '#fef3c7',  // yellow
      color: '#92400e',
    },
    'Assigned': {
      backgroundColor: '#e9d5ff',  // purple
      color: '#6b21a8',
    },
    'Awaiting Counsellor': {
      backgroundColor: '#dbeafe',  // blue
      color: '#1e40af',
    },
    'Awaiting Appointment': {
      backgroundColor: '#d1fae5',  // green
      color: '#065f46',
    },
    'Awaiting Session Notes': {
      backgroundColor: '#fed7aa',  // orange
      color: '#9a3412',
    },
    'Pending Archive': {
      backgroundColor: '#fed7aa',  // orange
      color: '#9a3412',
    },
    'Completed': {
      backgroundColor: '#bbf7d0',  // light green
      color: '#166534',
    },
    'Cancelled': {
      backgroundColor: '#fee2e2',  // red
      color: '#991b1b',
    },
    'Archived': {
      backgroundColor: '#e5e7eb',  // gray
      color: '#374151',
    }
  };

  const status = getClientStatus(client);
  return statusStyles[status];
};